import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext } from "react-router-dom";
import Cookies from "js-cookie";
import api, { getFreshAccessToken } from "../../chat/api";
import MyProjectDetailsModal from "./MyProjectDetailsModal";
import UnarchiveProjectModal from "./UnarchiveProjectModal";
import DeleteModal from "../KbFolder/DeleteModal";
import SuccessPopup from "../SuccessPopup";
import Pagination from "../../../components/Pagination/Pagination";
import { matchesDateRange } from "./projectDateUtils";
import { ProjectDetailsIcon, UnarchiveIcon, DeleteIcon } from "./MyProjectIcons";

// Dates arrive in mixed formats (DD/MMM/YYYY, DD/MM/YYYY, ISO) — mirrors
// SharedWithMe's parseDeadlineDate/formatDeadlineDate.
const parseDate = (dateString) => {
  if (!dateString || dateString === "_" || String(dateString).trim() === "") return null;
  const cleanStr = String(dateString).trim();

  if (cleanStr.match(/^\d{2}\/[a-zA-Z]{3}\/\d{4}$/)) {
    const [day, monthStr, year] = cleanStr.split("/");
    const monthMap = {
      jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
      jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
    };
    const monthNum = monthMap[monthStr.toLowerCase()];
    if (monthNum) return new Date(`${year}-${monthNum}-${day}T00:00:00`);
  }

  if (cleanStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [day, month, year] = cleanStr.split("/");
    return new Date(`${year}-${month}-${day}T00:00:00`);
  }

  const parsedDate = new Date(cleanStr);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatDate = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return "_";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}-${dd}-${yy}`;
};

const formatContractValue = (val) => {
  if (!val || val === "_" || val === "") return "_";
  return String(val).startsWith("$") ? val : `$${val}`;
};

const TH_CLASS =
  "px-4 text-[#00000080] font-outfit text-[13px] font-semibold border-b border-r border-[#0000001A]";
const TD_CLASS =
  "px-4 py-2 text-[#52525B] font-outfit text-[14px] font-medium border-b border-r border-[#0000001A]";

const COLUMNS = [
  { name: "name", label: "Project Name", width: 240 },
  { name: "project_lead", label: "Project Lead", width: 160 },
  { name: "date_submitted", label: "Date Submitted", width: 130 },
  { name: "contract_value", label: "Contract Value", width: 130 },
  { name: "archived_by", label: "Archived By", width: 140 },
  { name: "archived_at", label: "Archive Date", width: 120 },
  { name: "updated_at", label: "Last Edited", width: 120 },
  { name: "archive_reason", label: "Archive Reason", width: 260 },
];

const ArchivedProjects = () => {
  const navigate = useNavigate();
  const { searchQuery, activeFilters } = useOutletContext();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [isActionMenuOpen, setIsActionMenuOpen] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ top: 0, left: 0, openUpwards: false });
  const actionMenuRefs = useRef({});

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [projectForDetails, setProjectForDetails] = useState(null);

  const [isUnarchiveModalOpen, setIsUnarchiveModalOpen] = useState(false);
  const [projectToUnarchive, setProjectToUnarchive] = useState(null);
  const [unarchiveLoading, setUnarchiveLoading] = useState(false);
  const [unarchiveError, setUnarchiveError] = useState("");

  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteCompleted, setDeleteCompleted] = useState(false);

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [footerPortalNode, setFooterPortalNode] = useState(null);
  useEffect(() => {
    setFooterPortalNode(document.getElementById("projects-footer-portal"));
  }, []);

  const fetchArchivedProjects = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = await getFreshAccessToken();
      if (!token) return;

      const orgId = Cookies.get("selected_org_id");
      const url = orgId
        ? `/api/v1/rfp/folders/archived/?organization_id=${encodeURIComponent(orgId)}`
        : "/api/v1/rfp/folders/archived/";
      const response = await api.get(url, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data && response.data.folders) {
        const mapped = response.data.folders.map((project) => ({
          ...project,
          id: project.id || "_",
          name: project.name || "_",
          status: project.rfp_status || "Draft",
          contract_value: project.contract_value || "_",
          date_submitted: project.date_submitted || "_",
          deadline: project.deadline || "_",
        }));
        mapped.sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at));
        setProjects(mapped);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error("Error fetching archived projects:", error);
      if (!silent) setProjects([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArchivedProjects();
  }, [fetchArchivedProjects]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isActionMenuOpen !== null &&
        actionMenuRefs.current[isActionMenuOpen] &&
        !actionMenuRefs.current[isActionMenuOpen].contains(event.target) &&
        !event.target.closest(".action-button")
      ) {
        setIsActionMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isActionMenuOpen]);

  const filteredProjects = useMemo(() => {
    let result = projects;

    const query = (searchQuery || "").toLowerCase().trim();
    if (query) {
      result = result.filter((p) => (p.name || "").toLowerCase().includes(query));
    }

    if (activeFilters?.statuses?.length > 0) {
      const allowed = activeFilters.statuses.map((s) => s.toLowerCase());
      result = result.filter((p) => allowed.includes((p.status || "").toLowerCase()));
    }

    const now = new Date();
    if (activeFilters?.bidEndDate) {
      result = result.filter((p) => matchesDateRange(p.deadline, activeFilters.bidEndDate, now));
    }
    if (activeFilters?.dateSubmitted) {
      result = result.filter((p) => matchesDateRange(p.date_submitted, activeFilters.dateSubmitted, now));
    }

    return result;
  }, [projects, searchQuery, activeFilters]);

  const currentProjects = useMemo(() => {
    const last = currentPage * rowsPerPage;
    const first = last - rowsPerPage;
    return filteredProjects.slice(first, last);
  }, [filteredProjects, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredProjects.length / rowsPerPage);

  const paginationUI = useMemo(() => {
    if (filteredProjects.length === 0) return null;
    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredProjects.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    );
  }, [filteredProjects.length, currentPage, totalPages, rowsPerPage]);

  const toggleActionMenu = (item, event) => {
    event.stopPropagation();
    event.preventDefault();
    const menuHeight = 150;
    const rect = event.currentTarget.getBoundingClientRect();
    const bottomSpace = window.innerHeight - rect.bottom;
    const openUpwards = bottomSpace < menuHeight;

    setIsActionMenuOpen(isActionMenuOpen === item.id ? null : item.id);
    setActionMenuPosition({
      top: openUpwards ? rect.top - menuHeight : rect.bottom,
      left: rect.left - 160,
      openUpwards,
    });
  };

  const handleRowClick = (item) => {
    if (item.org_id) {
      navigate(`/rfp-projects/project/${item.id}/org/${item.org_id}`, {
        state: { folderId: item.id, folderName: item.name, orgId: item.org_id },
      });
    } else {
      navigate(`/rfp-projects/project/${item.id}`, {
        state: { folderId: item.id, folderName: item.name },
      });
    }
  };

  const handleActionDetails = (item) => {
    setIsActionMenuOpen(null);
    setProjectForDetails(item);
    setIsDetailsModalOpen(true);
  };

  const handleActionUnarchive = (item) => {
    setIsActionMenuOpen(null);
    setProjectToUnarchive(item);
    setUnarchiveError("");
    setIsUnarchiveModalOpen(true);
  };

  const confirmUnarchive = async () => {
    const folderId = projectToUnarchive?.id;
    if (!folderId) return;
    setUnarchiveLoading(true);
    setUnarchiveError("");
    try {
      const token = await getFreshAccessToken();
      await api.post(
        `/api/v1/rfp/folders/${folderId}/unarchive/`,
        {},
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setProjects((prev) => prev.filter((p) => p.id !== folderId));
      setIsUnarchiveModalOpen(false);
      setSuccessMessage(`"${projectToUnarchive.name}" was unarchived.`);
      setShowSuccessPopup(true);
      setProjectToUnarchive(null);
      fetchArchivedProjects(true);
    } catch (error) {
      const message = error?.response?.data?.error || "Failed to unarchive project.";
      setUnarchiveError(message);
    } finally {
      setUnarchiveLoading(false);
    }
  };

  const handleActionDelete = (item) => {
    setIsActionMenuOpen(null);
    setProjectToDelete(item);
    setDeleteCompleted(false);
    setConfirmDeleteVisible(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete?.id) return;
    setDeleteInProgress(true);
    try {
      const token = await getFreshAccessToken();
      await api.delete("/api/v1/rfp/folders/delete/", {
        data: { folder_ids: [projectToDelete.id] },
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });

      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete.id));
      setDeleteCompleted(true);
      setDeleteInProgress(false);

      setTimeout(() => {
        setConfirmDeleteVisible(false);
        setProjectToDelete(null);
        setDeleteCompleted(false);
        fetchArchivedProjects(true);
      }, 1500);

      setSuccessMessage("Project deleted.");
      setShowSuccessPopup(true);
    } catch (error) {
      setDeleteInProgress(false);
      console.error("Failed to delete archived project:", error);
    }
  };

  const colSpanCount = COLUMNS.length + 1;

  return (
    <>
      <div className="flex flex-col flex-1 overflow-hidden mt-2 w-full pl-2 pr-1 pb-2">
        <div className="w-full h-full flex flex-col border border-[#0000001A] rounded-lg">
          <div className="relative overflow-auto custom-scrollbar flex-1 rounded-lg bg-white">
            <table className="w-full min-w-[1500px] table-fixed text-left border-separate border-spacing-0">
              <thead className="h-[36px] sticky top-0 z-30 bg-[#F7F7F7]">
                <tr className="bg-[#F7F7F7]">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.name}
                      style={{ width: col.width, minWidth: col.width }}
                      className={TH_CLASS}
                    >
                      {col.label}
                    </th>
                  ))}
                  <th
                    style={{ width: "80px", minWidth: "80px" }}
                    className="px-2 text-[#00000080] font-outfit text-[13px] font-semibold text-center border-b border-[#0000001A]"
                  >
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white">
                {loading ? (
                  Array.from({ length: 8 }).map((_, rowIdx) => (
                    <tr key={`skeleton-${rowIdx}`} className="animate-pulse">
                      {COLUMNS.map((col, colIdx) => (
                        <td key={`skeleton-${rowIdx}-${col.name}`} className="px-4 py-[14px] border-b border-r border-[#0000001A]">
                          <div className="h-3.5 rounded bg-[#E5E7EB]" style={{ width: `${[70, 55, 60, 50, 60, 55, 60, 75][colIdx % 8]}%` }} />
                        </td>
                      ))}
                      <td className="px-2 py-[14px] border-b border-[#0000001A]">
                        <div className="h-3.5 w-5 rounded bg-[#E5E7EB] mx-auto" />
                      </td>
                    </tr>
                  ))
                ) : filteredProjects.length > 0 ? (
                  currentProjects.map((item, index) => {
                    // Mirrors backend permission tiers: owner or editor can act, reviewers view-only.
                    const canManage = item.is_owner || item.user_permission === "editor";
                    return (
                      <tr
                        key={item.id || index}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => handleRowClick(item)}
                      >
                        <td style={{ width: COLUMNS[0].width }} className={`${TD_CLASS} truncate`} title={item.name}>
                          {item.name || "_"}
                        </td>
                        <td style={{ width: COLUMNS[1].width }} className={`${TD_CLASS} truncate`} title={item.project_lead || undefined}>
                          {item.project_lead || "_"}
                        </td>
                        <td style={{ width: COLUMNS[2].width }} className={`${TD_CLASS} truncate`}>
                          {formatDate(item.date_submitted)}
                        </td>
                        <td style={{ width: COLUMNS[3].width }} className={`${TD_CLASS} truncate`}>
                          {formatContractValue(item.contract_value)}
                        </td>
                        <td style={{ width: COLUMNS[4].width }} className={`${TD_CLASS} truncate`} title={item.archived_by?.name || undefined}>
                          {item.archived_by?.name || "_"}
                        </td>
                        <td style={{ width: COLUMNS[5].width }} className={`${TD_CLASS} truncate`}>
                          {formatDate(item.archived_at)}
                        </td>
                        <td style={{ width: COLUMNS[6].width }} className={`${TD_CLASS} truncate`}>
                          {formatDate(item.updated_at)}
                        </td>
                        <td style={{ width: COLUMNS[7].width }} className={`${TD_CLASS} truncate`} title={item.archive_reason || undefined}>
                          {item.archive_reason || "_"}
                        </td>

                        <td
                          style={{ width: "80px", minWidth: "80px" }}
                          className="px-2 py-2 relative border-b border-[#0000001A] text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            onClick={(e) => toggleActionMenu(item, e)}
                            className="cursor-pointer inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 transition-colors action-button"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                              <path d="M1.66511 3.33333C1.20721 3.33333 0.815211 3.17014 0.489127 2.84375C0.163042 2.51736 0 2.125 0 1.66667C0 1.20833 0.163042 0.815972 0.489127 0.489583C0.815211 0.163194 1.20721 0 1.66511 0C2.12302 0 2.51501 0.163194 2.8411 0.489583C3.16718 0.815972 3.33022 1.20833 3.33022 1.66667C3.33022 2.125 3.16718 2.51736 2.8411 2.84375C2.51501 3.17014 2.12302 3.33333 1.66511 3.33333ZM6.66045 3.33333C6.20254 3.33333 5.81055 3.17014 5.48446 2.84375C5.15838 2.51736 4.99534 2.125 4.99534 1.66667C4.99534 1.20833 5.15838 0.815972 5.48446 0.489583C5.81055 0.163194 6.20254 0 6.66045 0C7.11835 0 7.51035 0.163194 7.83643 0.489583C8.16252 0.815972 8.32556 1.20833 8.32556 1.66667C8.32556 2.125 8.16252 2.51736 7.83643 2.84375C7.51035 3.17014 7.11835 3.33333 6.66045 3.33333ZM11.6558 3.33333C11.1979 3.33333 10.8059 3.17014 10.4798 2.84375C10.1537 2.51736 9.99067 2.125 9.99067 1.66667C9.99067 1.20833 10.1537 0.815972 10.4798 0.489583C10.8059 0.163194 11.1979 0 11.6558 0C12.1137 0 12.5057 0.163194 12.8318 0.489583C13.1579 0.815972 13.3209 1.20833 13.3209 1.66667C13.3209 2.125 13.1579 2.51736 12.8318 2.84375C12.5057 3.17014 12.1137 3.33333 11.6558 3.33333Z" fill="#71717A" />
                            </svg>
                          </div>

                          {isActionMenuOpen === item.id &&
                            createPortal(
                              <div
                                className="fixed z-[9999] bg-white rounded-xl shadow-lg p-1.5 min-w-[180px] max-h-80 overflow-y-auto action-menu-container border border-gray-100"
                                style={{
                                  top: `${actionMenuPosition.top}px`,
                                  left: `${actionMenuPosition.left}px`,
                                }}
                                ref={(el) => { if (el) actionMenuRefs.current[item.id] = el; }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div
                                  onClick={() => handleActionDetails(item)}
                                  className="group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-[#F4F4F5] transition-all duration-150 whitespace-nowrap"
                                >
                                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-[#52525B]">
                                    <ProjectDetailsIcon />
                                  </div>
                                  <span className="text-[15px] font-medium text-[#52525B]">Project Details</span>
                                </div>

                                {canManage && (
                                  <div
                                    onClick={() => handleActionUnarchive(item)}
                                    className="group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-[#F4F4F5] transition-all duration-150 whitespace-nowrap"
                                  >
                                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-[#52525B]">
                                      <UnarchiveIcon />
                                    </div>
                                    <span className="text-[15px] font-medium text-[#52525B]">Unarchive Project</span>
                                  </div>
                                )}

                                {canManage && (
                                  <div
                                    onClick={() => handleActionDelete(item)}
                                    className="group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-[#F4F4F5] transition-all duration-150 whitespace-nowrap"
                                  >
                                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-[#52525B]">
                                      <DeleteIcon />
                                    </div>
                                    <span className="text-[15px] font-medium text-[#52525B]">Delete</span>
                                  </div>
                                )}
                              </div>,
                              document.body
                            )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr className="hover:!bg-white hover:!shadow-none">
                    <td colSpan={colSpanCount} className="py-20 text-center border-b border-[#0000001A]">
                      <p className="text-[17px] font-outfit font-semibold text-[#1A1A1A] mb-1.5">
                        No archived projects
                      </p>
                      <p className="text-[14px] font-outfit text-[#71717A] max-w-[340px] mx-auto leading-relaxed">
                        Projects you archive will show up here.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <MyProjectDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => { setIsDetailsModalOpen(false); setProjectForDetails(null); }}
        projectData={projectForDetails}
      />

      <UnarchiveProjectModal
        isOpen={isUnarchiveModalOpen}
        onClose={() => { if (!unarchiveLoading) { setIsUnarchiveModalOpen(false); setProjectToUnarchive(null); setUnarchiveError(""); } }}
        onConfirm={confirmUnarchive}
        loading={unarchiveLoading}
        error={unarchiveError}
      />

      <DeleteModal
        isOpen={confirmDeleteVisible}
        onClose={() => { if (!deleteInProgress) { setConfirmDeleteVisible(false); setProjectToDelete(null); } }}
        onDelete={confirmDelete}
        fileName={""}
        loading={deleteInProgress}
        deleteSuccess={deleteCompleted}
      />

      {showSuccessPopup && (
        <SuccessPopup message={successMessage} onClose={() => setShowSuccessPopup(false)} />
      )}

      {footerPortalNode && createPortal(paginationUI, footerPortalNode)}
    </>
  );
};

export default ArchivedProjects;
